import { useMemo, useState } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import { MapPin } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { DialogFooter } from '../../../../../components/ui/dialog';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import type { SpatialColumnDeclarationFormat } from '../../utils/spatialValueAccessor';
import {
  FORMAT_OPTIONS,
  isNumericLike,
  type SpatialDeclareFormValues,
} from './declareSpatialFormMeta';

export type { SpatialDeclareFormValues };

type DeclareSpatialColumnFieldsProps = {
  dataType?: string;
  siblingColumns: string[];
  onConfirm: (values: SpatialDeclareFormValues) => void | Promise<void>;
  onCancel: () => void;
};

/**
 * Declare-mode body: the TanStack Form (format select → conditional lng-column /
 * axis-order) plus the Cancel/Declare footer. Split out of
 * `DeclareSpatialColumnDialog` to keep both files ≤200 lines. The
 * `feature:spatial_declare` track fires in the caller's `onConfirm`.
 */
export function DeclareSpatialColumnFields({
  dataType,
  siblingColumns,
  onConfirm,
  onCancel,
}: DeclareSpatialColumnFieldsProps) {
  const [busy, setBusy] = useState(false);

  const defaultValues = useMemo<SpatialDeclareFormValues>(
    () => ({
      format: isNumericLike(dataType) && siblingColumns.length > 0 ? 'lat-lng-pair' : 'wkt',
      lngColumn: siblingColumns[0] ?? '',
      axisOrder: 'lat-lng',
    }),
    [dataType, siblingColumns],
  );

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setBusy(true);
      try {
        await onConfirm(value);
      } finally {
        setBusy(false);
      }
    },
  });

  const format = useStore(form.store, (s) => s.values.format);

  // Disabled-with-reason: a pair needs a sibling column to serve as longitude.
  const blockedReason =
    format === 'lat-lng-pair' && siblingColumns.length === 0
      ? 'This table has no other column to use as the longitude. Choose WKT, GeoJSON, or single-column lat/lng text instead.'
      : null;

  return (
    <>
      <form
        id="declare-spatial-form"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="space-y-3"
      >
        <form.Field name="format">
          {(field) => (
            <div className="grid gap-1">
              <Label className="text-xs">Coordinate format</Label>
              <Select
                value={field.state.value}
                onValueChange={(v) => field.handleChange(v as SpatialColumnDeclarationFormat)}
              >
                <SelectTrigger data-testid="declare-spatial-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMAT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {FORMAT_OPTIONS.find((o) => o.value === field.state.value)?.hint}
              </p>
            </div>
          )}
        </form.Field>

        {format === 'lat-lng-pair' && siblingColumns.length > 0 && (
          <form.Field name="lngColumn">
            {(field) => (
              <div className="grid gap-1">
                <Label className="text-xs">Longitude column</Label>
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v)}>
                  <SelectTrigger data-testid="declare-spatial-lng-column">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {siblingColumns.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
        )}

        {format === 'latlng-text' && (
          <form.Field name="axisOrder">
            {(field) => (
              <div className="grid gap-1">
                <Label className="text-xs">Axis order</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as 'lat-lng' | 'lng-lat')}
                >
                  <SelectTrigger data-testid="declare-spatial-axis-order">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lat-lng">lat, lng (e.g. 52.23, 21.01)</SelectItem>
                    <SelectItem value="lng-lat">lng, lat (e.g. 21.01, 52.23)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
        )}
      </form>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="declare-spatial-cancel">
          Cancel
        </Button>
        {blockedReason ? (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button disabled data-testid="declare-spatial-confirm">
                  <MapPin className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                  Declare
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs whitespace-normal text-left">
              {blockedReason}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            type="submit"
            form="declare-spatial-form"
            disabled={busy}
            data-testid="declare-spatial-confirm"
          >
            {busy ? (
              <RandomDotmSpinner size={14} className="mr-2" />
            ) : (
              <MapPin className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            )}
            Declare
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
